import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client'; // Import io from socket.io-client
import StyledAlert from './components/StyledAlert'; // Import StyledAlert

const GroupChat = () => {
  const [messages, setMessages] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [chatImage, setChatImage] = useState(null);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState(true);
  const [alertMessage, setAlertMessage] = useState(""); // State for alert message
  const [alertCallback, setAlertCallback] = useState(null); // Callback for alert actions
  const [isUploading, setIsUploading] = useState(false); // State for spinner overlay
  const chatMessagesContainerRef = useRef(null);
  const loggedInUser = localStorage.getItem('userId');
  const socket = useRef(null);

  useEffect(() => {
    if (!loggedInUser) {
      window.location.href = 'login.html';
    }

    socket.current = io('https://socialserver-377n.onrender.com');
    socket.current.on('groupMessage', (message) => {
      appendMessage(message);
    });

    fetchGroupMessages();
    checkMessagingState();

    return () => {
      socket.current.disconnect();
    };
  }, [loggedInUser]); // Add loggedInUser to the dependency array

  const fetchGroupMessages = async () => {
    try {
      const response = await fetch('https://socialserver-377n.onrender.com/api/messages/group');
      const data = await response.json();
      if (response.ok && data.messages) {
        setMessages(data.messages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching group messages:', error.message);
    }
  };

  const appendMessage = (message) => {
    setMessages((prevMessages) => [...prevMessages, message]);
    if (chatMessagesContainerRef.current) {
      chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
    }
  };

  const checkMessagingState = async () => {
    try {
      const response = await fetch('https://socialserver-377n.onrender.com/api/admin/messaging-state');
      const result = await response.json();
      setIsMessagingEnabled(result.enabled);
      if (!result.enabled) {
        setAlertMessage("Messaging is currently disabled by the admin."); // Set alert message
      }
    } catch (error) {
      console.error('Error checking messaging state:', error);
    }
  };

  const handleChatFormSubmit = async (e) => {
    e.preventDefault();
    if (!isMessagingEnabled) {
        setAlertMessage("Messaging is currently disabled by the admin."); // Set alert message
        return;
    }

    if (!chatMessage.trim() && !chatImage) return;

    const formData = new FormData();
    formData.append('sender', loggedInUser);
    if (chatMessage.trim()) formData.append('text', chatMessage.trim());
    if (chatImage) {
        if (!chatImage.type.startsWith('image/')) { // Validate file type
            setAlertMessage("Only image files are supported.");
            return;
        }
        formData.append('image', chatImage);
    }

    try {
        const response = await fetch('https://socialserver-377n.onrender.com/api/messages/group', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to send message');
        }

        setChatMessage('');
        setChatImage(null);
        fetchGroupMessages();
    } catch (error) {
        console.error('Error sending message:', error.message);
        setAlertMessage(error.message); // Display error message in alert
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await fetch(`https://socialserver-377n.onrender.com/api/messages/group/${messageId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: loggedInUser }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete message');
      }

      setMessages((prevMessages) => prevMessages.filter((msg) => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error.message);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('sender', loggedInUser);
    formData.append('image', selectedFile);

    setIsUploading(true); // Show spinner overlay

    try {
      const response = await fetch('https://socialserver-377n.onrender.com/api/messages/group', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to send image');
      }

      setChatImage(null); // Clear the selected image
      fetchGroupMessages(); // Refresh messages after sending
    } catch (error) {
      console.error('Error sending image:', error.message);
    } finally {
      setIsUploading(false); // Hide spinner overlay
    }
  };

  const goBack = () => {
    window.location.href = '/sdb'; // Redirect to Sdb.js
  };

  const showFullImage = (imagePath) => {
    const modal = document.createElement('div');
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100%';
    modal.style.height = '100%';
    modal.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    modal.style.display = 'flex';
    modal.style.justifyContent = 'center';
    modal.style.alignItems = 'center';
    modal.style.zIndex = '1000';

    const fullImage = document.createElement('img');
    fullImage.src = imagePath;
    fullImage.style.maxWidth = '90%';
    fullImage.style.maxHeight = '90%';
    fullImage.style.borderRadius = '5px';

    modal.appendChild(fullImage);

    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    document.body.appendChild(modal);
  };

  useEffect(() => {
    if (chatMessagesContainerRef.current) {
      chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div style={styles.chatContainer}>
      {/* Spinner overlay */}
      {isUploading && <div style={styles.spinnerOverlay}><div style={styles.spinner}></div></div>}
      {/* StyledAlert */}
      {alertMessage && (
        <StyledAlert
          message={alertMessage}
          onClose={() => {
            setAlertMessage(""); // Clear alert
            setAlertCallback(null);
          }}
          onConfirm={alertCallback} // Execute callback if confirmed
        />
      )}
      <header style={styles.header}>
        <h2 style={styles.headerTitle}>Social Community</h2>
        <button style={styles.backButton} onClick={goBack}>
          Back
        </button>
      </header>
      <div id="chatMessagesContainer" style={styles.chatMessagesContainer} ref={chatMessagesContainerRef}>
        {messages.length > 0 ? (
          messages.map((message) => (
            <div key={message._id} style={styles.message}>
              <div style={styles.messageHeader}>
                <strong style={styles.messageSender}>
                  {message.sender?.fullName || "Unknown User"} ({message.sender?.email || "Unknown Email"})
                </strong>
                {message.sender?._id === loggedInUser && (
                  <button style={styles.deleteButton} onClick={() => handleDeleteMessage(message._id)}>
                    Delete
                  </button>
                )}
              </div>
              {message.text && <p style={styles.messageText}>{message.text}</p>}
              {message.image && (
                <img
                  src={message.image} // Use the secure_url directly
                  alt="User uploaded content"
                  style={styles.messageImage}
                  onClick={() => showFullImage(message.image)} // Add click handler for full view
                />
              )}
            </div>
          ))
        ) : (
          <div style={styles.noMessages}>No messages yet</div>
        )}
      </div>
      <form style={styles.form} onSubmit={handleChatFormSubmit}>
        <input
          type="text"
          name="chatMessage"
          placeholder="Type your message..."
          value={chatMessage}
          onChange={(e) => setChatMessage(e.target.value)}
          style={styles.input}
        />
        <div style={styles.fileInputContainer}>
          <input
            type="file"
            name="chatImage"
            accept="image/*"
            onChange={handleFileChange}
            style={styles.fileInput}
          />
          <button
            type="button"
            style={styles.uploadButton}
            onClick={() => document.querySelector('input[name="chatImage"]').click()}
          >
            Image
          </button>
        </div>
        <button type="submit" style={styles.sendButton}>
          Send
        </button>
      </form>
    </div>
  );
};

// Spinner keyframes moved outside the styles object
const spinKeyframes = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Inject keyframes into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = spinKeyframes;
  document.head.appendChild(styleSheet);
}

const styles = {
  chatContainer: {
    height: '100vh',
    width: '100vw',
    margin: '0',
    padding: '10px',
    background: '#1f4037',
    color: 'white',
    fontFamily: "'Poppins', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative', // Ensure relative positioning for child elements
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    padding: '0 10px',
  },
  chatMessagesContainer: {
    width: '100%',
    background: 'rgba(0,0,0,0.2)',
    padding: '10px',
    maxHeight: 'calc(100vh - 120px)', // Adjust height to account for header and form
    overflowY: 'auto',
    borderRadius: '5px',
    marginBottom: '0', // Remove margin to touch the form
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
  },
  message: {
    marginBottom: '10px',
    padding: '5px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  messageSender: {
    fontSize: '0.9em',
    color: '#ccc',
  },
  deleteButton: {
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontSize: '0.8em',
  },
  messageText: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    margin: '5px 0',
    color: 'white',
  },
  messageImage: {
    maxWidth: '200px',
    borderRadius: '5px',
    marginTop: '5px',
    cursor: 'pointer', // Indicate clickable image
  },
  noMessages: {
    textAlign: 'center',
    color: '#ccc',
  },
  form: {
    width: '100%',
    display: 'flex',
    alignItems: 'center', // Align items vertically
    gap: '10px',
    justifyContent: 'space-between',
    position: 'absolute', // Position the form at the bottom
    bottom: '10px', // Add spacing from the bottom
    left: '0',
    padding: '10px',
    backgroundColor: '#1f4037', // Match the background color
    boxShadow: '0 -2px 6px rgba(0, 0, 0, 0.2)', // Add shadow for depth
  },
  input: {
    flex: '1',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '1em',
    height: '40px',
    maxWidth: '70%', // Make input area longer
  },
  fileInputContainer: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '15%', // Shorten upload image and send button
  },
  fileInput: {
    display: 'none',
  },
  uploadButton: {
    width: '150%', // Increased width
    height: '40px',
    background: '#007bff',
    border: 'none',
    borderRadius: '5px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '1em',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '100%',
  },
  sendButton: {
    width: '100%',
    height: '40px',
    background: '#07a85d',
    border: 'none',
    borderRadius: '5px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '1em',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '15%', // Shorten upload image and send button
  },
  backButton: {
    padding: '10px 15px',
    background: '#007bff', // Blue background
    border: 'none',
    borderRadius: '5px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '1em',
    fontWeight: 'bold',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)', // Add shadow for depth
    transition: 'background 0.3s ease, transform 0.2s',
  },
  backButtonHover: {
    background: '#0056b3', // Darker blue on hover
    transform: 'translateY(-2px)', // Slight lift on hover
  },
  spinnerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid rgba(255, 255, 255, 0.3)',
    borderTop: '5px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite', // Ensure animation is applied
  },
};

export default GroupChat;